// App.js - the routes (from logistics-workflow's App.js): public pages, the learners' play links, and the protected
// workspace under /app/* (pages/MainPage.js). The Game Builder's players open in their own tabs under /game-builder/*.
import React, { useEffect, useRef, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "react-hot-toast";
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import AcceptInvitePage from "./pages/AcceptInvitePage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import BookDemoPage from "./pages/BookDemoPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import MainPage from "./pages/MainPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TryDemoPage from "./pages/TryDemoPage";
import ErrorBoundary from "./components/ErrorBoundary";
import LoadingOverlay from "./components/common/LoadingOverlay";
import OrientationGate from "./components/common/OrientationGate";
import GuestPlay from "./game-builder/components/GuestPlay";
import { PlanPlayPage, PlayPage, TryOutPage } from "./game-builder/components/GamePages";
import { MobileProvider } from "./context/MobileContext";
import errorTracker from "./services/errorTracking";
import setupAxiosInterceptors from "./services/axiosInterceptor";
import setupTokenRefresh from "./services/tokenRefresh";
import analytics from "./services/analytics";
import { toastOptions } from "./styles/index";

const API_BASE = process.env.REACT_APP_API_URL;

function RouteTransitionLoader() {
  const location = useLocation();
  const isFirstRender = useRef(true);
  const [showLoader, setShowLoader] = useState(false);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return undefined;
    }
    setShowLoader(true);
    const timer = setTimeout(() => setShowLoader(false), 250);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  return showLoader ? <LoadingOverlay message="Loading page..." /> : null;
}

function PageViewTracker() {
  const location = useLocation();
  useEffect(() => {
    // /app paths are tracked by MainPage.js with their tab's title
    if (location.pathname.startsWith("/app")) return;
    analytics.pageView(location.pathname + location.search, document.title);
  }, [location.pathname, location.search]);
  return null;
}

errorTracker.initialize({
  enabled: true,
  environment: process.env.NODE_ENV,
  enableConsole: process.env.NODE_ENV === "development",
  sendToServer: false,
});
setupAxiosInterceptors();
if (process.env.REACT_APP_GA4_MEASUREMENT_ID) {
  analytics.initialize(process.env.REACT_APP_GA4_MEASUREMENT_ID);
}

const App = () => {
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [checkingToken, setCheckingToken] = useState(true);

  const logout = (redirectPath = null) => {
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("avatarEmoji");
    setToken(null);
    setCheckingToken(false);
    errorTracker.clearUser();
    analytics.setUserId(null);
    analytics.event("logout");
    if (redirectPath) window.location.assign(redirectPath);
  };

  // An expired login token is renewed quietly (services/tokenRefresh.js); a refused renewal signs out.
  useEffect(() => setupTokenRefresh({ onExpired: () => logout() }), []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!token) {
      setCheckingToken(false);
      return;
    }
    setCheckingToken(true);
    axios
      .get(`${API_BASE}/validate-token`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        if (!res.data.valid) return logout();
        setCheckingToken(false);
        errorTracker.setUser(res.data.user);
        if (res.data.user?.user_id) {
          analytics.setUserId(res.data.user.user_id);
          analytics.setUserProperties({ role: res.data.user.role });
        }
        return undefined;
      })
      .catch(() => logout());
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogin = (newToken, avatar, refreshToken) => {
    setCheckingToken(true);
    localStorage.setItem("token", newToken);
    if (refreshToken) localStorage.setItem("refreshToken", refreshToken);
    else localStorage.removeItem("refreshToken");          // a try-it session isn't renewed
    if (avatar) localStorage.setItem("avatarEmoji", avatar);
    setToken(newToken);
  };

  const Protected = ({ children }) => {
    if (checkingToken) return <LoadingOverlay message="Checking login..." />;
    return token ? children : <Navigate to="/login" replace />;
  };
  const PublicOnly = ({ children }) => (token ? <Navigate to="/app" replace /> : children);

  return (
    <>
      <ErrorBoundary>
        <MobileProvider>
          <Router>
            <PageViewTracker />
            <RouteTransitionLoader />
            <Routes>
              {/* Public */}
              <Route path="/" element={<PublicOnly><LandingPage /></PublicOnly>} />
              <Route path="/login" element={<PublicOnly><LoginPage onLogin={handleLogin} /></PublicOnly>} />
              <Route path="/register" element={<PublicOnly><RegisterPage /></PublicOnly>} />
              <Route path="/accept-invite" element={<PublicOnly><AcceptInvitePage onLogin={handleLogin} /></PublicOnly>} />
              <Route path="/try-demo" element={<PublicOnly><TryDemoPage onLogin={handleLogin} /></PublicOnly>} />
              <Route path="/forgot-password" element={<PublicOnly><ForgotPasswordPage /></PublicOnly>} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/book-demo" element={<BookDemoPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />

              {/* Learners: a published game from its share link - no account */}
              <Route path="/play/:token" element={<><OrientationGate /><GuestPlay /></>} />

              {/* The workspace */}
              <Route path="/app/*" element={<Protected><MainPage token={token} logout={logout} /></Protected>} />

              {/* The Game Builder's players, each in its own tab (GameBuilder.jsx opens them) - landscape only on touch screens */}
              <Route path="/game-builder/try" element={<><OrientationGate /><Protected><TryOutPage /></Protected></>} />
              <Route path="/game-builder/play/:planId/:missionId" element={<><OrientationGate /><Protected><PlayPage /></Protected></>} />
              <Route path="/game-builder/story/:planId" element={<><OrientationGate /><Protected><PlanPlayPage what="story" /></Protected></>} />
              <Route path="/game-builder/site/:planId" element={<><OrientationGate /><Protected><PlanPlayPage what="site" /></Protected></>} />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Router>
        </MobileProvider>
      </ErrorBoundary>
      <Toaster toastOptions={toastOptions} />
    </>
  );
};

export default App;
